import { AfterContentChecked, Component, ElementRef, ErrorHandler, Input, OnChanges, OnInit, SimpleChanges, ViewChild, Inject } from '@angular/core';
import { compareDesc, format, isSameMonth, isSameYear, parseISO, parseJSON } from 'date-fns';
import { faCheck, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp, faSort, faSortDown, faSortUp, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { BetService } from 'src/app/bet.service';
import { BlockConfigurationObject } from 'src/app/block-configuration-object.model';
import { GeneralService } from 'src/app/general.service';
import { MatchService } from 'src/app/match.service';
import { SportDataService } from 'src/app/sport-data.service';
import { LocalisationService } from 'src/app/localisation.service';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';

@Component({
	selector: 'app-golf-tournament-block',
	templateUrl: './golf-tournament-block.component.html',
	styleUrls: ['./golf-tournament-block.component.scss']
})
export class GolfTournamentBlockComponent implements OnInit, OnChanges, AfterContentChecked {

	@Input() configurationObject: BlockConfigurationObject;
	@ViewChild("blockBody") blockBody: ElementRef;

	blockInitStarted = false;
	displayMode = "";
	tournamentLoading = true;
	tournamentError = false;
	tournamentData = null;
	golfRoundsCompleted = 0;
	golfFuturesStage = "pre";
	bookOptions = [environment.sportExclusiveBookmakers?.["GOLF"]] || environment.bookmakers;
	golfFuturesMarket = "winner";
	stageDropdownOpen = false;
	tournamentActiveTab = "futures";
	playerSearch = "";
	futuresPlayerLimit = 20;
	h2hPairLimit = 5;
	threeBallGroupLimit = 5;
	futuresPlayerSortVariable = "position";
	futuresPlayerSortReversed = false;
	temperatureDisplay = environment.temperatureDisplay;
	tournamentID = "";
	tournamentSeason = "";
	futuresLastUpdated = "";
	eligibleFuturesPlayers = [];
	displayedFuturesPlayers = [];
	eligibleH2HPairs = [];
	displayedH2HPairs = [];
	eligible3BallGroups = [];
	displayed3BallGroups = [];

	environment = environment;

	displayMaximumWidths = {
		"x-compact": 350,
		compact: 500,
		"less-compact": 600,
		medium: 700,
		large: 800,
		"x-large": 1000,
	};

	iconConversionDict = {
		"clear-day": "wsymbol_0001_sunny",
		"clear-night": "wsymbol_0008_clear_sky_night",
		"partly-cloudy-day": "wsymbol_0002_sunny_intervals",
		"partly-cloudy-night": "wsymbol_0002_sunny_intervals",
		cloudy: "wsymbol_0003_white_cloud",
		rain: "wsymbol_0009_light_rain_showers",
		sleet: "wsymbol_0021_cloudy_with_sleet",
		snow: "wsymbol_0012_heavy_snow_showers",
		wind: "wsymbol_0001_sunny",
		fog: "wsymbol_0007_fog",
	};

	marketConversionChart = {
		winner: "GOLF_TOURNAMENT_STROKE_WINNER",
		top10: "GOLF_TOURNAMENT_STROKE_TOP_10",
		top20: "GOLF_TOURNAMENT_STROKE_TOP_20",
		top5: "GOLF_TOURNAMENT_STROKE_TOP_5",
	};

	iconAltTextDict = {
		"clear-day": "Clear",
		"clear-night": "Clear",
		"partly-cloudy-day": "Partly Cloudy",
		"partly-cloudy-night": "Partly Cloudy",
		cloudy: "Cloudy",
		rain: "Rain",
		sleet: "Sleet",
		snow: "Snow",
		wind: "Wind",
		fog: "Fog",
	};

	countryLookup = {
		ALBANIA: "ALB",
		ARGENTINA: "ARG",
		AUSTRALIA: "AUS",
		AUSTRIA: "AUT",
		BAHAMAS: "BHS",
		BAHRAIN: "BHR",
		BANGLADESH: "BGD",
		BELGIUM: "BEL",
		BERMUDA: "BMU",
		BOTSWANA: "BWA",
		BRAZIL: "BRA",
		CANADA: "CAN",
		"CAYMAN ISLANDS": "CYM",
		CHILE: "CHI",
		CHINA: "CHN",
		"CHINESE TAIPEI": "TWN",
		COLOMBIA: "COL",
		"COSTA RICA": "CRI",
		"CZECH REPUBLIC": "CZE",
		DENMARK: "DEN",
		"DOMINICAN REPUBLIC": "DOM",
		ECUADOR: "ECU",
		ENGLAND: "ENG",
		ESTONIA: "EST",
		FIJI: "FIJ",
		FINLAND: "FIN",
		FRANCE: "FRA",
		GERMANY: "DEU",
		GUATEMALA: "GTM",
		"HONG KONG": "HKG",
		ICELAND: "ISL",
		INDIA: "IND",
		INDONESIA: "IDN",
		IRELAND: "IRL",
		"ISLE OF MAN": "IMN",
		ITALY: "ITA",
		JAPAN: "JPN",
		JORDAN: "JOR",
		KENYA: "KEN",
		MALAYSIA: "MYS",
		MAURITIUS: "MUS",
		MEXICO: "MEX",
		MONACO: "MCO",
		MOROCCO: "MAR",
		MYANMAR: "MMR",
		NAMIBIA: "NAM",
		NETHERLANDS: "NED",
		"NEW ZEALAND": "NZL",
		"NORTHERN IRELAND": "NIR",
		NORWAY: "NOR",
		OMAN: "OMN",
		PAKISTAN: "PAK",
		PANAMA: "PAN",
		PARAGUAY: "PAR",
		PERU: "PER",
		PHILIPPINES: "PHL",
		POLAND: "POL",
		PORTUGAL: "PRT",
		"PUERTO RICO": "PRI",
		QATAR: "QAT",
		"SAUDI ARABIA": "SAU",
		SCOTLAND: "SCO",
		SERBIA: "SRB",
		SINGAPORE: "SGP",
		SLOVAKIA: "SVK",
		"SOUTH AFRICA": "RSA",
		"SOUTH KOREA": "KOR",
		SPAIN: "ESP",
		"SRI LANKA": "LKA",
		SWAZILAND: "SWZ",
		SWEDEN: "SWE",
		SWITZERLAND: "CHE",
		TAIWAN: "TWN",
		TANZANIA: "TZA",
		THAILAND: "THA",
		TURKEY: "TUR",
		UGANDA: "UGA",
		"UNITED ARAB EMIRATES": "ARE",
		"UNITED STATES": "USA",
		USA: "USA",
		VENEZUELA: "VEN",
		VIETNAM: "VNM",
		WALES: "WAL",
		ZAMBIA: "ZMB",
		ZIMBABWE: "ZIM",
	};

	faChevronUp = faChevronUp;
	faChevronDown = faChevronDown;
	faCheck = faCheck;
	faTimesCircle = faTimesCircle;
	faSearch = faSearch;
	faSort = faSort;
	faSortUp = faSortUp;
	faSortDown = faSortDown;

	constructor(
		private errorHandler: ErrorHandler,
		public matchService: MatchService,
		public sportDataService: SportDataService,
		public generalService: GeneralService,
		public betService: BetService,
		public localisationService: LocalisationService,
		private el: ElementRef,
		@Inject(DOCUMENT) public document: Document,
	) {}

	prepareBlock(): void {
		this.blockInitStarted = true;
		this.generalService.initialiseBlock(this.el);

		if (typeof window !== "undefined") {
			window.addEventListener("resize", this.checkCompact.bind(this));
			window.addEventListener("click", this.closeOpenDropdowns.bind(this));
		}

		this.matchService.getGeoData().then(() => {
			this.retrieveTournamentData(this.configurationObject.season, this.configurationObject.tournamentID);
		}, () => {
			this.retrieveTournamentData(this.configurationObject.season, this.configurationObject.tournamentID);
		});
	}

	ngOnInit(): void {
		if (this.configurationObject && !this.blockInitStarted) {
			this.prepareBlock();
		}

	}

	ngAfterContentChecked(): void {

		const element = this.el?.nativeElement;
		if (element && !this.displayMode) {
			this.displayMode = "full";
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					this.displayMode += ` ${level}`;
				}
			});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.blockInitStarted && changes.configurationObject.currentValue) {
			this.prepareBlock();
		} else if (changes.configurationObject.currentValue && changes.configurationObject.previousValue
			&& changes.configurationObject.currentValue.tournamentID !== changes.configurationObject.previousValue.tournamentID) {
			this.prepareBlock();
		}
	}

	closeOpenDropdowns(event: MouseEvent): void {
		if ((event.target as Element).closest(".stage-dropdown") === null) {
			this.stageDropdownOpen = false;
		}
	}

	clearPlayerSearch(): void {
		this.playerSearch = "";
		this.setAllPlayerLists();
	};

	setGolfFuturesMarket(market: string): void {
		this.golfFuturesMarket = market;
		this.setDisplayedFuturesPlayers();
	};

	setTournamentActiveTab(tab: string): void {
		this.tournamentActiveTab = tab;
	};

	toggleStageDropdown(): void {
		this.stageDropdownOpen = !this.stageDropdownOpen;
	};

	clearFuturesPlayerLimit(): void {
		this.futuresPlayerLimit = undefined;
		this.setDisplayedFuturesPlayers();
	};

	resetFuturesPlayerLimit(): void {
		this.futuresPlayerLimit = 20;
		this.setDisplayedFuturesPlayers();
	}

	clearH2HPairLimit = (): void => {
		this.h2hPairLimit = undefined;
		this.setDisplayedH2HPairs();
	};

	resetH2HPairLimit(): void {
		this.h2hPairLimit = 5;
		this.setDisplayedH2HPairs();
	}

	clearThreeBallGroupLimit(): void {
		this.threeBallGroupLimit = undefined;
		this.setDisplayed3BallGroups();
	}

	resetThreeBallGroupLimit(): void {
		this.threeBallGroupLimit = 5;
		this.setDisplayed3BallGroups();
	}


	checkCompact(): void {
		let modeString = "full";
		const element = this.blockBody.nativeElement;
		if (element) {
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					modeString += ` ${level}`;
				}
			});
		}


		this.displayMode = modeString;
	}

	probConversion(value: number, numPctDigits: number): string {
		return (value * 100).toFixed(numPctDigits) + "%";
	}

	roundDataAvailable(roundNo: number): boolean {
		return this.tournamentData.FuturesData
			&& this.tournamentData.FuturesData
				.some(p => typeof p["totalScore_R" + roundNo] !== "undefined");
	}

	setFuturesPlayerSort(variable: string): void {
		if (this.futuresPlayerSortVariable === variable) {
			// toggle reverse of sort
			this.futuresPlayerSortReversed = !this.futuresPlayerSortReversed;
		} else {
			this.futuresPlayerSortVariable = variable;
			this.futuresPlayerSortReversed = false;
		}
		this.setDisplayedFuturesPlayers();
	}

	futuresPlayerSort(a, b): number {
		if (this.futuresPlayerSortVariable === "position") {
			if (this.golfRoundsCompleted === 0) {
				let aProb: number;
				let bProb: number;
				if (this.golfFuturesStage === "pre") {
					aProb = a[this.golfFuturesMarket] || 0;
					bProb = b[this.golfFuturesMarket] || 0;
				}

				if (this.golfFuturesStage === "after1") {
					aProb = a[this.golfFuturesMarket + "_R1"] || 0;
					bProb = b[this.golfFuturesMarket + "_R1"] || 0;
				}

				if (this.golfFuturesStage === "after2") {
					aProb = a[this.golfFuturesMarket + "_R2"] || 0;
					bProb = b[this.golfFuturesMarket + "_R2"] || 0;
				}

				if (this.golfFuturesStage === "after3") {
					aProb = a[this.golfFuturesMarket + "_R3"] || 0;
					bProb = b[this.golfFuturesMarket + "_R3"] || 0;
				}

				if (this.futuresPlayerSortReversed) {
					return aProb - bProb;
				}

				return bProb - aProb;
			} else {
				let aPos: number; let bPos: number;

				if (this.golfFuturesStage === "pre") {
					aPos = 9999;
					bPos = 9999;
				}

				if (this.golfFuturesStage === "after1") {
					aPos = a.currentPos_R1 || 9999;
					bPos = b.currentPos_R1 || 9999;
				}

				if (this.golfFuturesStage === "after2") {
					aPos = a.currentPos_R2 || 9999;
					bPos = b.currentPos_R2 || 9999;
				}

				if (this.golfFuturesStage === "after3") {
					aPos = a.currentPos_R3 || 9999;
					bPos = b.currentPos_R3 || 9999;
				}

				if (aPos === 9999 && bPos === 9999 && this.golfFuturesStage !== "pre") {
					let aTotal = 0;
					let bTotal = 0;

					if (this.golfFuturesStage === "after1") {
						aTotal = a.totalScore_R1;
						bTotal = b.totalScore_R1;
					}

					if (this.golfFuturesStage === "after2") {
						aTotal = a.totalScore_R2;
						bTotal = b.totalScore_R2;
					}

					if (this.golfFuturesStage === "after3") {
						aTotal = a.totalScore_R3;
						bTotal = b.totalScore_R3;
					}
					if (this.futuresPlayerSortReversed) {
						return bTotal - aTotal;
					}

					return aTotal - bTotal;

				}

				if (this.futuresPlayerSortReversed) {
					return bPos - aPos;
				}

				return aPos - bPos;

			}
		}

		if (this.futuresPlayerSortVariable === "probability") {
			let aProb: number;
			let bProb: number;
			if (this.golfFuturesStage === "pre") {
				aProb = a[this.golfFuturesMarket];
				bProb = b[this.golfFuturesMarket];
			}

			if (this.golfFuturesStage === "after1") {
				aProb = a[this.golfFuturesMarket + "_R1"] || 0;
				bProb = b[this.golfFuturesMarket + "_R1"] || 0;
			}

			if (this.golfFuturesStage === "after2") {
				aProb = a[this.golfFuturesMarket + "_R2"] || 0;
				bProb = b[this.golfFuturesMarket + "_R2"] || 0;
			}

			if (this.golfFuturesStage === "after3") {
				aProb = a[this.golfFuturesMarket + "_R3"] || 0;
				bProb = b[this.golfFuturesMarket + "_R3"] || 0;
			}

			if (this.futuresPlayerSortReversed) {
				return aProb - bProb;
			}

			return bProb - aProb;

		}

		return 0;
	}

	retrieveTournamentData(season: string, tournamentID: string): void {
		this.tournamentLoading = true;
		this.tournamentError = false;

		this.sportDataService.getGolfTournamentData(tournamentID, season)
			.subscribe((tournamentData) => {
				this.tournamentData = tournamentData;

				if (this.tournamentData.FuturesData) {
					if (this.tournamentData.FuturesData.some(p => typeof p.roundScore_R3 !== "undefined")) {
						this.golfRoundsCompleted = 3;
						this.golfFuturesStage = "after3";
					} else if (this.tournamentData.FuturesData.some(p => typeof p.roundScore_R2 !== "undefined")) {
						this.golfRoundsCompleted = 2;
						this.golfFuturesStage = "after2";
					} else if (this.tournamentData.FuturesData.some(p => typeof p.roundScore_R1 !== "undefined")) {
						this.golfRoundsCompleted = 1;
						this.golfFuturesStage = "after1";
					} else {
						this.golfRoundsCompleted = 0;
						this.golfFuturesStage = "pre";
					}
				} else {
					this.golfRoundsCompleted = 0;
					this.golfFuturesStage = "pre";
				}

				if (this.tournamentData.WeatherInfo) {
					this.tournamentData.WeatherInfo.forEach(day => {
						if (Object.keys(this.iconConversionDict).includes(day.icon)) {
							day.newIcon = `https://content-blocks-node.azurewebsites.net/static/assets/weather-icons/${
								this.iconConversionDict[day.icon]
							}.svg`;
							day.description = this.iconAltTextDict[day.icon];
						} else {
							day.newIcon =
								"https://content-blocks-node.azurewebsites.net/static/assets/weather-icons/wsymbol_0999_unknown.svg";
							day.description = "Unknown";
						}
					});
				}

				if (this.tournamentData.FuturesData) {
					const latestLastUpdated: Date = this.tournamentData.FuturesData.map(p => parseJSON(p.lastUpdated)).sort(compareDesc)[0];
					this.futuresLastUpdated = format(latestLastUpdated, "h:mma, MMM d");

					this.setDisplayedFuturesPlayers();
				} else {
					this.futuresLastUpdated = "Not Available";
				}

				this.tournamentData.tournamentPrize = new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				}).format(this.tournamentData.tournamentPrize);

				if (this.tournamentData.ThreeBall) {

					this.tournamentData.ThreeBall.forEach(group => {
						group.formattedTeeTime = format(parseJSON(group.teeTime), "HH:mm");
					});

					this.setDisplayed3BallGroups();

				}

				if (this.tournamentData.H2HMatrix) {
					this.setDisplayedH2HPairs();
				}

				this.tournamentLoading = false;
			}, (err) => {
				if (err !== "API_CALL_FAILED") {
					this.errorHandler.handleError(err);
				}
				this.tournamentError = true;
			});
	}

	setDisplayedFuturesPlayers(): void {
		if (this.tournamentData?.FuturesData) {
			this.eligibleFuturesPlayers = this.tournamentData.FuturesData
				.filter(p => this.fitsPlayerSearch(p))
				.sort((a,b) => this.futuresPlayerSort(a,b));

			this.displayedFuturesPlayers = this.futuresPlayerLimit ? this.eligibleFuturesPlayers.slice(0, this.futuresPlayerLimit)
				: this.eligibleFuturesPlayers;
		}
		
	}

	setDisplayedH2HPairs(): void {
		if (this.tournamentData?.H2HMatrix) {
			this.eligibleH2HPairs = this.tournamentData.H2HMatrix
				.filter(p => this.fitsH2HPlayerSearch(p));

			this.displayedH2HPairs = this.h2hPairLimit ? this.eligibleH2HPairs.slice(0, this.h2hPairLimit) : this.eligibleH2HPairs;
		}
		
	}

	setDisplayed3BallGroups(): void {
		if (this.tournamentData?.ThreeBall) {
			this.eligible3BallGroups = this.tournamentData.ThreeBall
				.filter(g => this.fits3BallPlayerSearch(g));

			this.displayed3BallGroups = this.threeBallGroupLimit ? this.eligible3BallGroups.slice(0, this.threeBallGroupLimit)
				: this.eligible3BallGroups;
		}
		
	}

	setAllPlayerLists(): void {
		this.setDisplayedFuturesPlayers();
		this.setDisplayedH2HPairs();
		this.setDisplayed3BallGroups();
	}

	tournamentDateString(): string {
		if (!this.tournamentLoading && !this.tournamentError) {
			const start = parseISO(this.tournamentData.startDate);
			const end = parseISO(this.tournamentData.endDate);
			if (isSameMonth(start, end)) {
				return format(start, "d") + " - " + format(end, "d MMMM yyyy");
			}

			if (isSameYear(start, end)) {
				return format(start, "d MMMM") + " - " + format(end, "d MMMM yyyy");
			}

			return format(start, "d MMMM yyyy") + " - " + format(end, "d MMMM yyyy");
		}

		return "";
	}

	fitsPlayerSearch(player): boolean {
		if (this.playerSearch === "") {
			return true;
		}

		return (player.playerFirstName && player.playerFirstName.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (player.playerLastName && player.playerLastName.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (player.playerCountry && player.playerCountry.toLowerCase().includes(this.playerSearch.toLowerCase()));
	}

	fitsH2HPlayerSearch(pair): boolean {
		if (this.playerSearch === "") {
			return true;
		}

		return (pair.player1Name && pair.player1Name.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (pair.player1Country && pair.player1Country.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (pair.player2Name && pair.player2Name.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (pair.player2Country && pair.player2Country.toLowerCase().includes(this.playerSearch.toLowerCase()));
	}

	fits3BallPlayerSearch(group): boolean {
		if (this.playerSearch === "") {
			return true;
		}

		return (group.player1Name && group.player1Name.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (group.player1Country && group.player1Country.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (group.player2Name && group.player2Name.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (group.player2Country && group.player2Country.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (group.player3Name && group.player3Name.toLowerCase().includes(this.playerSearch.toLowerCase()))
			|| (group.player3Country && group.player3Country.toLowerCase().includes(this.playerSearch.toLowerCase()));
	}

	probDisplay(player, market: string): string {
		let rawValue = 0;

		if (this.golfFuturesStage === "pre") {
			rawValue = player[market];
			// if passed in probability is not a number, return a dash
			if (typeof rawValue !== "number") {
				return "-";
			}

			// if number would get rounded to 100%, but isn't, change to "more than" highest visible number below 100%
			if (parseFloat((rawValue * 100).toFixed(1)) === 100 && rawValue !== 1) {
				return ">99.9%";
			}

			// if number would get rounded to 0%, OR is exactly zero, change to "less than" lowest visible number above 0%
			if (parseFloat((rawValue * 100).toFixed(1)) === 0) {
				return "<0.1%";
			}

			// otherwise, return the number converted to a percentage, to the specified number of decimal places
			return (rawValue * 100).toFixed(1) + "%";
		}

		if (this.golfFuturesStage === "after1") {
			rawValue = player[market + "_R1"];
		}

		if (this.golfFuturesStage === "after2") {
			rawValue = player[market + "_R2"];
		}

		if (this.golfFuturesStage === "after3") {
			rawValue = player[market + "_R3"];
		}

		return this.generalService.hedgedProbString(rawValue, 1);
	}

	probChange(player, market: string): string {
		let rawValue = 0;
		let oldValue = 0;

		if (this.golfFuturesStage === "after1") {
			rawValue = player[market + "_R1"];
			oldValue = player[market];
		}

		if (this.golfFuturesStage === "after2") {
			rawValue = player[market + "_R2"];
			oldValue = player[market + "_R1"];
		}

		if (this.golfFuturesStage === "after3") {
			rawValue = player[market + "_R3"];
			oldValue = player[market + "_R2"];
		}

		if (!oldValue) {
			return "";
		}

		if (rawValue === oldValue) {
			return this.probConversion(0, 0);
		}

		if (rawValue > oldValue) {
			return "+" + this.probConversion(Math.abs(rawValue - oldValue), 0);
		} else {
			return "-" + this.probConversion(Math.abs(rawValue - oldValue), 0);
		}
	}

	probChangeColour(player, market: string): string {
		let rawValue = 0;
		let oldValue = 0;

		if (this.golfFuturesStage === "after1") {
			rawValue = player[market + "_R1"];
			oldValue = player[market];
		}

		if (this.golfFuturesStage === "after2") {
			rawValue = player[market + "_R2"];
			oldValue = player[market + "_R1"];
		}

		if (this.golfFuturesStage === "after3") {
			rawValue = player[market + "_R3"];
			oldValue = player[market + "_R2"];
		}

		if (!rawValue) {
			return "";
		}

		if (rawValue === oldValue) {
			return "level";
		}

		if (rawValue > oldValue) {
			return "improved";
		} else {
			return "declined";
		}
	}

	playerPosition(player): string {
		let positionNumber: number;
		let tied: boolean;
		if (this.golfFuturesStage === "pre") {
			return "-";
		}

		if (this.golfFuturesStage === "after1") {
			positionNumber = player.currentPos_R1;
			tied = this.tournamentData.FuturesData
				.some(p => p.currentPos_R1 === positionNumber
					&& p.playerId !== player.playerId);
		}

		if (this.golfFuturesStage === "after2") {
			positionNumber = player.currentPos_R2;
			tied = this.tournamentData.FuturesData
				.some(p => p.currentPos_R2 === positionNumber
					&& p.playerId !== player.playerId);
		}

		if (this.golfFuturesStage === "after3") {
			positionNumber = player.currentPos_R3;
			tied = this.tournamentData.FuturesData
				.some(p => p.currentPos_R3 === positionNumber
					&& p.playerId !== player.playerId);
		}

		if (typeof positionNumber === "undefined") {
			return "MC";
		}

		return `${tied ? "T" : ""}${positionNumber}`;
	}

	playerScore(player, status): string {
		let score: number;
		if (status === "pre") {
			return "-";
		} else if (status === "after1") {
			score = player.totalScore_R1;
		} else if (status === "after2") {
			score = player.totalScore_R2;
		} else if (status === "after3") {
			score = player.totalScore_R3;
		} else {
			return "-";
		}

		if (score === 0) {
			return "E";
		} else if (!score) {
			return "";
		}

		if (score > 0) {
			return "+" + score;
		}

		return score.toString();
	}

	playerRoundScore(player, status: string): string {
		let score: number;
		if (status === "pre") {
			return "-";
		} else if (status === "after1") {
			score = player.roundScore_R1;
		} else if (status === "after2") {
			score = player.roundScore_R2;
		} else if (status === "after3") {
			score = player.roundScore_R3;
		} else {
			return "-";
		}

		if (score === 0) {
			return "E";
		} else if (!score) {
			return "";
		}

		if (score > 0) {
			return "+" + score;
		}

		return score.toString();
	}

	overlayWidth(): number {
		return (document.querySelector(".external-golf-tournament .futures-list-ctr .overlay-column") as HTMLElement).offsetWidth;
	}

	// rowHeight(index: number): number {
	// 	// calculate height of tallest child in left pane
	// 	const leftPane = $(".external-golf-tournament .futures-list-ctr .overlay-column .overlay-row").eq(index);
	// 	let leftMaxHeight = 0;
	// 	leftPane.children().each(function() {
	// 		if ($(this).outerHeight(true) > rightMaxHeight) {
	// 			leftMaxHeight = $(this).outerHeight(true);
	// 		}
	// 	});

	// 	// calculate height of tallest child in right pane
	// 	const rightPane = $(".external-golf-tournament .futures-list-ctr .player-list .player-row .scroll-section").eq(index);
	// 	let rightMaxHeight = 0;
	// 	rightPane.children().each(function() {
	// 		if ($(this).outerHeight(true) > rightMaxHeight) {
	// 			rightMaxHeight = $(this).outerHeight(true);
	// 		}
	// 	});

	// 	if (leftMaxHeight > rightMaxHeight) {
	// 		return leftMaxHeight;
	// 	} else {
	// 		return rightMaxHeight;
	// 	}

	// }

	reloadTournamentData(season: string, tournamentID: string): void {
		this.retrieveTournamentData(season, tournamentID);
	}

	isCurrentRound(): boolean {
		if (this.golfFuturesStage === "after3") { return this.golfRoundsCompleted === 3; }
		if (this.golfFuturesStage === "after2") { return this.golfRoundsCompleted === 2; }
		if (this.golfFuturesStage === "after1") { return this.golfRoundsCompleted === 1; }

		return this.golfRoundsCompleted === 0;
	}

	formattedBestFuturesOdds(player, market: string): string {
		let marketName = this.marketConversionChart[market];
		if (this.golfFuturesStage === "after1") {
			marketName = marketName + "_R1";
		}
		if (this.golfFuturesStage === "after2") {
			marketName = marketName + "_R2";
		}
		if (this.golfFuturesStage === "after3") {
			marketName = marketName + "_R3";
		}
		if (player && player.FuturesMarkets && player.FuturesMarkets.some(m => m.name === marketName)) {
			const matchingMarket = player.FuturesMarkets.find(m => m.name === marketName);
			if (matchingMarket.BettingData) {
				const matchingMarketBooks = Object.keys(matchingMarket.BettingData)
					.filter(k => this.bookOptions.includes(k) && matchingMarket.BettingData[k].price);
				if (matchingMarketBooks.length > 0) {
					let bestOdds = 0;
					matchingMarketBooks.forEach(key => {
						if (matchingMarket.BettingData[key].price > bestOdds) {
							bestOdds = matchingMarket.BettingData[key].price;
						}
					});

					return this.betService.formatOdds(bestOdds);
				} else {
					return "-";
				}

			} else {
				return "-";
			}
		} else {
			return "-";
		}
	}

	bestFuturesOddsBookmaker(player, market: string): string {
		let marketName = this.marketConversionChart[market];
		if (this.golfFuturesStage === "after1") {
			marketName = marketName + "_R1";
		}
		if (this.golfFuturesStage === "after2") {
			marketName = marketName + "_R2";
		}
		if (this.golfFuturesStage === "after3") {
			marketName = marketName + "_R3";
		}
		if (player && player.FuturesMarkets && player.FuturesMarkets.some(m => m.name === marketName)) {
			const matchingMarket = player.FuturesMarkets.find(m => m.name === marketName);
			if (matchingMarket.BettingData) {
				const matchingMarketBooks = Object.keys(matchingMarket.BettingData)
					.filter(k => this.bookOptions.includes(k) && matchingMarket.BettingData[k].price);
				if (matchingMarketBooks.length > 0) {
					let bestOdds = 0;
					let bestBook = "";
					matchingMarketBooks.forEach(key => {
						if (matchingMarket.BettingData[key].price > bestOdds) {
							bestOdds = matchingMarket.BettingData[key].price;
							bestBook = key;
						}
					});

					return bestBook;
				} else {
					return "";
				}
			} else {
				return "";
			}

		} else {
			return "";
		}
	}

	futuresValueOpacity(player, market: string): number {
		let marketName = this.marketConversionChart[market];
		let valueThreshold: number;
		if (market === "winner") {
			valueThreshold = 0.01;
		} else {
			valueThreshold = 0.05;
		}
		if (this.golfFuturesStage === "after1") {
			marketName = marketName + "_R1";
		}
		if (this.golfFuturesStage === "after2") {
			marketName = marketName + "_R2";
		}
		if (this.golfFuturesStage === "after3") {
			marketName = marketName + "_R3";
		}
		if (player && player.FuturesMarkets && player.FuturesMarkets.some(m => m.name === marketName)) {
			const matchingMarket = player.FuturesMarkets.find(m => m.name === marketName);
			if (matchingMarket.BettingData) {
				const matchingMarketBooks = Object.keys(matchingMarket.BettingData)
					.filter(k => this.bookOptions.includes(k) && matchingMarket.BettingData[k].price);
				if (matchingMarketBooks.length > 0) {
					let bestOdds = 0;
					matchingMarketBooks.forEach(key => {
						if (matchingMarket.BettingData[key].price > bestOdds) {
							bestOdds = matchingMarket.BettingData[key].price;
						}
					});

					const marketProb = 1 / bestOdds;

					let probability;

					if (this.golfFuturesStage === "pre") {
						probability = player[market];
					}

					if (this.golfFuturesStage === "after1") {
						probability = player[market + "_R1"];
					}

					if (this.golfFuturesStage === "after2") {
						probability = player[market + "_R2"];
					}

					if (this.golfFuturesStage === "after3") {
						probability = player[market + "_R3"];
					}

					if (probability - marketProb >= valueThreshold) {
						return 1;
					}
				} else {
					return 0;
				}
			} else {
				return 0;
			}

		} else {
			return 0;
		}
	}

	formattedBestH2HOdds(pair, player: number): string {
		// TODO
		return "+500";
	}

	bestH2HOddsBookmaker(pair, player: number): string {
		// TODO
		return "betstreet";
	}

	h2hValueOpacity(group, player: number): number {
		// TODO
		return 1;
	}

	formattedBest3BallOdds(group, player: number): string {
		// TODO
		return "+500";
	}

	best3BallOddsBookmaker(group, player: number): string {
		// TODO
		return "betstreet";
	}

	threeBallValueOpacity(group, player: number): number {
		// TODO
		return 1;
	}

	setGolfFuturesStage(stage: string): void {
		this.golfFuturesStage = stage;
		this.stageDropdownOpen = false;
		this.setDisplayedFuturesPlayers();
	}

}